import { Box, CircularProgress, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { entityKeys } from "../../../../../api/queryKeys";
import ExportButton from "../../../../../components/buttons/ExportButton";
import CustomToolTip from "../../../../../components/CustomTooltip";
import SearchInput from "../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../hooks/useFileNaming";

const InstalledProgramsView = ({ entityData }) => {
  const gridRef = useGridApiRef();
  const { fetchEntityInstalledPrograms, trackEvent } = useAPI();
  const { generateBreezeFileName } = useFileNaming();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  const { data: programsData, isLoading } = useQuery({
    queryKey: entityKeys.entityInstalledPrograms(entityData?.entity_id),
    queryFn: () => fetchEntityInstalledPrograms(entityData?.entity_id),
    enabled: !!entityData?.entity_id,
    select: (response) => response?.data?.programs || [],
  });

  const allPrograms = programsData || [];

  let filteredPrograms = [];
  if (appliedFilters.searchString !== "") {
    const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
    filteredPrograms = allPrograms.filter(
      (row) =>
        row?.name?.toLowerCase().includes(lowerCaseSearchString) ||
        row?.vendor?.toLowerCase().includes(lowerCaseSearchString) ||
        row?.version?.toLowerCase().includes(lowerCaseSearchString)
    );
  } else {
    filteredPrograms = allPrograms;
  }

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for installed programs - ${entityData?.display_name}`
    );
    const entityName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "entity";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`${entityName}_installed_programs`),
    };
    gridRef.current?.exportDataAsCsv(csvExportOptions);
  };

  const columnsToShow = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.1,
      renderCell: (params) => (
        <CustomToolTip title={params?.value || "-"}>
          <Typography
            variant="caption"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              width: "100%",
            }}
          >
            {params?.value}
          </Typography>
        </CustomToolTip>
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 0.8,
      renderCell: (params) => (
        <CustomToolTip title={params?.value || "-"}>
          <Typography
            variant="caption"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              width: "100%",
            }}
          >
            {params?.value}
          </Typography>
        </CustomToolTip>
      ),
    },
    {
      field: "version",
      headerName: "Version",
      flex: 0.8,
      renderCell: (params) => (
        <CustomToolTip title={params?.value || "-"}>
          <Typography
            variant="caption"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              width: "100%",
            }}
          >
            {params?.value}
          </Typography>
        </CustomToolTip>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!allPrograms || allPrograms.length === 0) {
    return (
      <Typography variant="body2" fontWeight={"600"} mt={1}>
        No installed programs available
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ width: "300px" }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredPrograms || filteredPrograms.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          apiRef={gridRef}
          columns={columnsToShow}
          rows={filteredPrograms}
        />
      </Box>
    </>
  );
};

export default InstalledProgramsView;
